import {
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import { DatasetRecord } from "../DatasetRecord";

interface UseDatasetsQueryOptions {
  orgId: string;
  limit?: number;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
  excludeDatasetIds?: string[];
}

/**
 * Tanstack query key for querying datasets for an org
 */
export function getQueryKey(options: UseDatasetsQueryOptions) {
  const {
    orgId,
    limit = 50,
    sortBy = "created",
    sortDirection = "DESC",
    excludeDatasetIds,
  } = options;
  return ["datasets", orgId, sortBy, sortDirection, limit, excludeDatasetIds];
}

/**
 * Hook to query datasets with pagination support.
 *
 * Fetches datasets for an organization with configurable sorting and pagination.
 * Uses React Query's infinite query pattern for efficient pagination.
 *
 * @param options - Configuration options
 * @param options.orgId - Organization ID to fetch datasets from
 * @param options.limit - Number of datasets per page (default: 50)
 * @param options.sortBy - Field to sort by (default: "created")
 * @param options.sortDirection - Sort direction (default: "DESC")
 *
 * @example
 * ```tsx
 * const datasetsQuery = useDatasetsQuery({
 *   orgId: "org_123",
 *   limit: 50,
 * });
 * ```
 */
export function useDatasetsQuery(options: UseDatasetsQueryOptions) {
  const { datasetService } = useDomainServices();
  const {
    orgId,
    limit = 50,
    sortBy = "created",
    sortDirection = "DESC",
  } = options;

  return useInfiniteQuery({
    queryKey: getQueryKey(options),
    queryFn: ({ pageParam, signal }) => {
      return datasetService.queryDatasets(
        {
          sort_direction: sortDirection,
          sort_by: sortBy,
          limit,
          after: pageParam,
        },
        { orgId, abortSignal: signal },
      );
    },
    select: (data) => {
      // Flatten pages into single array
      return data.pages.flatMap((page) => page.items);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<DatasetRecord>) =>
      lastPage.next_token,
    enabled: !!orgId,
  });
}

/**
 * Hook to query datasets with pagination support using Suspense.
 *
 * This hook uses React Query's useInfiniteSuspenseQuery which integrates with
 * React Suspense for loading states and Error Boundaries for error handling.
 * The component using this hook should be wrapped in a Suspense boundary with
 * a fallback UI for loading states, and an Error Boundary for error handling.
 *
 * Fetches datasets for an organization with configurable sorting and pagination.
 * Uses React Query's infinite query pattern for efficient pagination.
 *
 * @param options - Configuration options
 * @param options.orgId - Organization ID to fetch datasets from
 * @param options.limit - Number of datasets per page (default: 50)
 * @param options.sortBy - Field to sort by (default: "created")
 * @param options.sortDirection - Sort direction (default: "DESC")
 * @param options.excludeDatasetIds - Optional array of dataset IDs to exclude from results
 *
 * @example
 * ```tsx
 * function DatasetList({ orgId }) {
 *   const datasetsQuery = useInfiniteSuspenseDatasetsQuery({ orgId });
 *
 *   return (
 *     <div>
 *       {datasetsQuery.data.map(dataset => (
 *         <div key={dataset.dataset_id}>{dataset.name}</div>
 *       ))}
 *       {datasetsQuery.hasNextPage && (
 *         <button onClick={() => datasetsQuery.fetchNextPage()}>
 *           Load More
 *         </button>
 *       )}
 *     </div>
 *   );
 * }
 *
 * // Usage with Suspense and ErrorBoundary
 * <ErrorBoundary fallback={<ErrorUI />}>
 *   <Suspense fallback={<LoadingUI />}>
 *     <DatasetList orgId="org_123" />
 *   </Suspense>
 * </ErrorBoundary>
 * ```
 */
export function useInfiniteSuspenseDatasetsQuery(
  options: UseDatasetsQueryOptions,
) {
  const { datasetService } = useDomainServices();
  const {
    orgId,
    limit = 50,
    sortBy = "created",
    sortDirection = "DESC",
    excludeDatasetIds,
  } = options;

  return useSuspenseInfiniteQuery({
    queryKey: getQueryKey(options),
    queryFn: ({ pageParam, signal }) => {
      return datasetService.queryDatasets(
        {
          sort_direction: sortDirection,
          sort_by: sortBy,
          limit,
          after: pageParam,
        },
        { orgId, abortSignal: signal },
      );
    },
    select: (data): DatasetRecord[] => {
      // Flatten pages into single array
      const allDatasets = data.pages.flatMap((page) => page.items);

      // Filter out excluded datasets (client-side filtering)
      if (excludeDatasetIds && excludeDatasetIds.length > 0) {
        const excludeSet = new Set(excludeDatasetIds);
        return allDatasets.filter((d) => !excludeSet.has(d.dataset_id));
      }

      return allDatasets;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<DatasetRecord>) =>
      lastPage.next_token,
  });
}
